/**
 * Nag: push the responsible person to get it done (docs/BACKLOG.md B19; owner, 2026-09-28: "Mom with kids needs
 * results." "No warm water means you'll get it done now.").
 *
 * A manager (or a household adult) taps **Nag** on something overdue or urgent. The app picks who can do it now, writes
 * the text, and hands it to **Hit Em Up** (the owner's texting app; owner decision 2026-09-28: "We will integrate only
 * with Hit Em Up for SMS"), so a person sends it from their own account. Until Hit Em Up's compose link is configured
 * (HITEMUP_COMPOSE_URL), the sheet shows the text with "Copy the message"; no sms: links anywhere. If
 * nobody finishes in the window, the ladder names the next step: the next person on shift, then the person in charge,
 * then the owner. Done means the log or task was saved, never a reply.
 *
 * | Rule | Why (negation) |
 * |---|---|
 * | N1 Only people who can do it now: on shift, cleared for work today, not on leave | Texting someone who was excluded pressures a sick person to come in (Food Code 2-201.12) |
 * | N2 Someone not available shows only "Not available today", never why; the text never carries a reason | Naming the reason discloses health information to coworkers (T4, PEOPLE_AND_NEGATION) |
 * | N3 Urgent = a safety problem (no warm water, no soap, fridge above 41°F, sanitizer failed, an urgent report) and says so plainly | "Please log it" for no warm water reads as routine: handwashing stops |
 * | N4 At most one nag per person per 15 min (urgent) or 60 min (normal); normal nags wait for open hours | A text every minute, or at 11 pm for a routine log, is harassment and people mute the app |
 * | N5 Done = the item's record exists, so the ladder stops there | A reply ("on it!") that never becomes a log would stop the ladder while the fridge stays warm |
 *
 * Pure functions: the API, the web app and the phone app all build the same targets, words and links (T7).
 */
import { firstName } from "./actions.ts";
import { namesLine } from "./handoff.ts";
import { incidentInfo } from "./incidents.ts";
import type { Id, IsoDate, IsoDateTime } from "./model.ts";
import { expectedChecks, isSatisfied, shiftGate, type DayContext } from "./schedule.ts";
import { shiftHoursIso } from "./hours.ts";
import { getTemplate } from "./templates.ts";

const ILLNESS_LABEL = incidentInfo("illness").label;

export type NagUrgency = "normal" | "urgent";
/** What a nag points at: a due check (logs and tasks), a failed record that needs its fix, an open report, or a home chore. */
export type NagKind = "check" | "fix" | "report" | "chore";
export const NAG_KINDS: readonly NagKind[] = ["check", "fix", "report", "chore"];

/** Minutes to wait before the ladder moves on, and between two nags to one person (N4). */
export const NAG_WINDOW_MIN: Record<NagUrgency, number> = { urgent: 15, normal: 60 };

/** Where the web app lives; links in texts point here so they open on any phone, app or not. */
export const NAG_WEB_BASE = "https://app.cleancleaver.com/app";
/** The phone app's scheme (apps/mobile app.config.ts). */
export const NAG_PHONE_SCHEME = "logwave://";

/** "check:cold-4h@11:00", "fix:<entryId>", "report:<incidentId>", "chore:<choreId>". */
export const itemRef = (kind: NagKind, id: string) => `${kind}:${id}`;

export function parseItemRef(ref: string): { kind: NagKind; id: string } | null {
  const i = ref.indexOf(":");
  if (i <= 0) return null;
  const kind = ref.slice(0, i) as NagKind;
  const id = ref.slice(i + 1);
  return NAG_KINDS.includes(kind) && id.length > 0 && id.length <= 200 ? { kind, id } : null;
}

/** The thing to get done, as the text names it. */
export interface NagItem {
  ref: string;
  kind: NagKind;
  /** "Fridge and freezer check", "Clean the toilet". */
  title: string;
  /** Local clock time it was due, "2:00 PM" (checks). */
  dueLabel?: string;
  dueAt?: IsoDateTime;
  urgency: NagUrgency;
  /** Urgent only: what's wrong, in plain words ("no warm water at the hand sink"). */
  problem?: string;
  /** The record exists (log saved, fix written, report resolved, chore done): the ladder stops (N5). */
  done?: boolean;
}

/* ─────────────── urgency (N3) ─────────────── */

/** Failed-rule ids (templates.ts) that are safety problems, and how the text says them. */
export const URGENT_RULES: Record<string, string> = {
  rr_water: "no warm water at the hand sink",
  rr_soap: "no soap at the hand sink",
  fridge: "a fridge is above 41°F",
  cold_hold: "cold food is above 41°F",
  chlorine: "the sanitizer failed its test",
  quat: "the sanitizer failed its test",
  iodine: "the sanitizer failed its test",
  hot: "the hot-water sanitizing is below 171°F",
  // Food Code 3-501.14(A): a missed cooling stage means the food is reheated or thrown out now (core cooling.ts).
  cool_2h: "cooked food didn't cool to 70°F in 2 hours",
  cool_6h: "cooked food didn't cool to 41°F in 6 hours",
};

/** The first safety problem among failed rules, in plain words; undefined when none is urgent. */
export function urgentProblem(ruleIds: readonly string[]): string | undefined {
  for (const r of ruleIds) if (URGENT_RULES[r]) return URGENT_RULES[r];
  return undefined;
}

/* ─────────────── building items ─────────────── */

/** A due check (a log or a checklist task, core schedule.ts ExpectedCheck). */
export function checkNagItem(e: { key: string; label: string; dueAt?: IsoDateTime }, o: { dueLabel?: string; done?: boolean } = {}): NagItem {
  return { ref: itemRef("check", e.key), kind: "check", title: e.label, urgency: "normal", ...(e.dueAt ? { dueAt: e.dueAt } : {}), ...(o.dueLabel ? { dueLabel: o.dueLabel } : {}), ...(o.done ? { done: true } : {}) };
}

/** A failed record waiting for "what was done" (a failed_check Finding). Urgent when a failed rule is a safety problem. */
export function fixNagItem(entryId: Id, title: string, ruleIds: readonly string[], o: { done?: boolean } = {}): NagItem {
  const problem = urgentProblem(ruleIds);
  return { ref: itemRef("fix", entryId), kind: "fix", title, urgency: problem ? "urgent" : "normal", ...(problem ? { problem } : {}), ...(o.done ? { done: true } : {}) };
}

/** A sick report is private (T4): nobody is ever nagged about it, and its words never reach a coworker. */
export function isPrivateReport(r: { type?: string; title?: string }): boolean {
  return r.type === "illness" || (!!r.title && r.title.startsWith(ILLNESS_LABEL));
}

/**
 * An open report. An urgent report (severity urgent) nags as urgent, with its title as the problem. A sick report gives
 * undefined: it's private, and handled by the person in charge alone.
 */
export function reportNagItem(incidentId: Id, title: string, severity: string, o: { type?: string; done?: boolean } = {}): NagItem | undefined {
  if (isPrivateReport({ type: o.type, title })) return undefined;
  const urgent = severity === "urgent";
  return { ref: itemRef("report", incidentId), kind: "report", title, urgency: urgent ? "urgent" : "normal", ...(urgent ? { problem: title.replace(/[.\s]+$/, "") } : {}), ...(o.done ? { done: true } : {}) };
}

/** A home chore; the link opens its step-by-step guide. */
export function choreNagItem(c: { id: string; title: string }, o: { done?: boolean } = {}): NagItem {
  return { ref: itemRef("chore", c.id), kind: "chore", title: c.title, urgency: "normal", ...(o.done ? { done: true } : {}) };
}

/** A Finding (schedule.ts) as a nag item, or undefined for findings nobody can be nagged about (e.g. people's gates). */
export function findingNagItem(f: { kind: string; ref?: string; title: string; severity: string; ruleIds?: string[] }): NagItem | undefined {
  if (!f.ref) return undefined;
  if (f.kind === "missed_check") return checkNagItem({ key: f.ref, label: f.title.replace(/^Still needed:\s*/, "") });
  if (f.kind === "failed_check") return fixNagItem(f.ref, f.title.replace(/^Needs a fix:\s*/, ""), f.ruleIds ?? []);
  if (f.kind === "incident") return reportNagItem(f.ref, f.title, f.severity);
  return undefined;
}

/* ─────────────── links ─────────────── */

/**
 * Straight to the item after sign-in. Web: https://app.cleancleaver.com/app/do?check=KEY, /do/fix/ID,
 * /today?report=ID, /rooms/guide/CHORE. Phone: logwave://open?check=KEY (and fix=, report=, chore=), which the phone's
 * `open` route keeps through sign-in.
 */
export function nagLink(ref: string, surface: "web" | "phone" = "web", base = NAG_WEB_BASE): string {
  const p = parseItemRef(ref);
  if (!p) throw new Error(`Not a nag item: ${ref}`);
  const id = encodeURIComponent(p.id);
  if (surface === "phone") return `${NAG_PHONE_SCHEME}open?${p.kind}=${id}`;
  const b = base.replace(/\/+$/, "");
  switch (p.kind) {
    case "check": return `${b}/do?check=${id}`;
    case "fix": return `${b}/do/fix/${id}`;
    case "report": return `${b}/today?report=${id}`;
    case "chore": return `${b}/rooms/guide/${id}`;
  }
}

/**
 * Reads a nag link back (web or phone form) into the item it opens. Anything else (another page, another host, a bad
 * id) gives null, so a link can never open a screen it didn't name.
 */
export function parseNagLink(url: string): { kind: NagKind; id: string } | null {
  let u: URL;
  try { u = new URL(url); } catch { return null; }
  const ok = (kind: NagKind, id: string | null | undefined) => (id ? parseItemRef(`${kind}:${id}`) : null);
  if (u.protocol === "logwave:") {
    // logwave://open?check=KEY parses with host "open"; logwave:///open?… with pathname "/open".
    const where = (u.host || u.pathname.replace(/^\/+/, "")).replace(/\/+$/, "");
    if (where !== "open" && where !== "do") return null;
    for (const k of NAG_KINDS) { const r = ok(k, u.searchParams.get(k)); if (r) return r; }
    return null;
  }
  if (u.protocol !== "https:" && u.protocol !== "http:") return null;
  const path = u.pathname.replace(/^\/app(?=\/)/, "").replace(/\/+$/, "");
  if (path === "/do") return ok("check", u.searchParams.get("check"));
  const fix = /^\/do\/fix\/([^/]+)$/.exec(path);
  if (fix) return ok("fix", decodeURIComponent(fix[1]!));
  if (path === "/today") return ok("report", u.searchParams.get("report"));
  const chore = /^\/rooms\/guide\/([^/]+)$/.exec(path);
  if (chore) return ok("chore", decodeURIComponent(chore[1]!));
  return null;
}

/** Where the phone app goes for an item (expo-router paths). */
export function phoneRouteFor(p: { kind: NagKind; id: string }): string {
  const id = encodeURIComponent(p.id);
  switch (p.kind) {
    case "check": return `/do?check=${id}`;
    case "fix": return `/today?fix=${id}`;
    case "report": return `/today?report=${id}`;
    case "chore": return `/home-guide?id=${id}`;
  }
}

/* ─────────────── who (N1, N2) ─────────────── */

export interface NagPerson {
  id: Id;
  name: string;
  phone?: string;
  isPersonInCharge?: boolean;
}

/** A leave on the books (dates inclusive). No leave table exists yet; callers pass what they know (e.g. an ended volunteer). */
export interface NagLeave { employeeId: Id; from: IsoDate; to?: IsoDate }

export interface NagTargets {
  /** Who can do it now, the suggested person first: on shift and not in charge, then the person in charge. */
  available: NagPerson[];
  /** On shift but not available. Only the words "Not available today": never why (N2). */
  unavailable: { id: Id; name: string; line: string }[];
}

export const NOT_AVAILABLE = "Not available today";

/**
 * People who can do `item` now (N1). `clearance[id]` is true when that person is OK to work today (core shiftGate
 * ready: checked in healthy, cleared). Excluded, restricted, not-yet-checked-in and on-leave people are never targeted;
 * they're listed as unavailable with no reason. `fromId` (the sender) is left out.
 */
export function nagTargets(o: { item: Pick<NagItem, "kind">; onShift: NagPerson[]; clearance: Record<Id, boolean>; leave?: NagLeave[]; now: IsoDateTime | Date; today?: IsoDate; fromId?: Id }): NagTargets {
  const now = typeof o.now === "string" ? o.now : o.now.toISOString();
  const today = o.today ?? now.slice(0, 10);
  const away = new Set((o.leave ?? []).filter((l) => l.from <= today && (!l.to || today <= l.to)).map((l) => l.employeeId));
  const available: NagPerson[] = [];
  const unavailable: NagTargets["unavailable"] = [];
  for (const p of o.onShift) {
    if (p.id === o.fromId) continue;
    if (o.clearance[p.id] === true && !away.has(p.id)) available.push(p);
    else unavailable.push({ id: p.id, name: p.name, line: NOT_AVAILABLE });
  }
  // The person doing the shift's work first; the person in charge is the ladder's second step.
  available.sort((a, b) => Number(!!a.isPersonInCharge) - Number(!!b.isPersonInCharge));
  return { available, unavailable };
}

/* ─────────────── words ─────────────── */

/** The text, in plain words. Built only from the item, the person's first name and the sender: never a health reason (N2). */
export function nagMessage(o: { item: Pick<NagItem, "kind" | "title" | "dueLabel" | "problem">; person?: Pick<NagPerson, "name"> | { names: string[] }; from?: string; urgency: NagUrgency; link: string }): string {
  const sign = o.from ? ` — ${firstName(o.from)}` : "";
  if (o.urgency === "urgent") {
    const problem = (o.item.problem ?? o.item.title).replace(/[.\s]+$/, "");
    return `Urgent: ${problem}. Please fix now: ${o.link}${sign}`;
  }
  const who = !o.person ? "" : "names" in o.person ? namesLine(o.person.names) : firstName(o.person.name);
  const hi = who ? `Hi ${who}, ` : "Hi, ";
  const title = o.item.title.replace(/[.\s]+$/, "");
  switch (o.item.kind) {
    case "check": return `${hi}the ${o.item.dueLabel ? `${o.item.dueLabel} ` : ""}check is due: ${title}. Tap to log it: ${o.link}${sign}`;
    case "fix": return `${hi}this needs a fix: ${title}. Tap to write what was done: ${o.link}${sign}`;
    case "report": return `${hi}this report is still open: ${title}. Tap to open it: ${o.link}${sign}`;
    case "chore": return `${hi}time for this: ${title}. Here are the steps: ${o.link}${sign}`;
  }
}

/* ─────────────── the channel: Hit Em Up only ─────────────── */

/** Hit Em Up's contract limits (docs/HITEMUP.md, "Limits"): checked here too, so nothing is silently cut. */
export const HITEMUP_MAX_RECIPIENTS = 50;
export const HITEMUP_MAX_BODY = 1000;
/** The `source` Hit Em Up's analytics see. */
export const HITEMUP_SOURCE = "cleancleaver";
/** A name or group name in the link is at most this long (contract). */
export const HITEMUP_MAX_NAME = 60;

/** Which team a group text goes to; `gref` is `<locationId>:<kind>` so Hit Em Up reuses the same group next time. */
export type HitemupGroupKind = "team" | "on-shift";
export interface HitemupGroup { business: string; locationId: string; kind: HitemupGroupKind }
const GROUP_LABEL: Record<HitemupGroupKind, string> = { team: "Team", "on-shift": "On shift" };

const clip = (s: string, n: number) => (s.length <= n ? s : `${s.slice(0, n - 1).trimEnd()}…`);
/** One name for the link: the record's display name, one line, no commas (the list is comma-separated), at most 60 characters. */
const linkName = (s: string) => clip(s.replace(/[,\s]+/g, " ").trim(), HITEMUP_MAX_NAME);
/** "Business – Team", at most 60 characters (the business part is shortened, the label never). */
export function hitemupGroupName(g: Pick<HitemupGroup, "business" | "kind">): string {
  const tail = ` – ${GROUP_LABEL[g.kind]}`;
  return `${clip(g.business.replace(/\s+/g, " ").trim(), HITEMUP_MAX_NAME - tail.length)}${tail}`;
}

/**
 * A stored phone as E.164 ("+18015550101"), or undefined when it can't be made one. US default (NANP): 10 digits get
 * "+1"; 11 digits starting with 1 get "+". A leading "+" means the person already gave a country code: 8 to 15 digits,
 * no leading 0 (+1 must be followed by exactly 10 digits, area code starting 2 to 9). Spaces, dashes, dots and
 * parentheses are ignored; letters or anything else make it invalid. Hit Em Up rejects the whole link for one bad number.
 */
export function toE164(raw: string | undefined): string | undefined {
  const t = (raw ?? "").trim();
  if (!t || /[^\d\s().+-]/.test(t) || (t.includes("+") && !t.startsWith("+")) || t.indexOf("+") !== t.lastIndexOf("+")) return undefined;
  const d = t.replace(/\D/g, "");
  const nanp = (n: string) => /^[2-9]\d{9}$/.test(n);
  if (t.startsWith("+")) {
    if (d.startsWith("1")) return nanp(d.slice(1)) && d.length === 11 ? `+${d}` : undefined;
    return /^[1-9]\d{7,14}$/.test(d) ? `+${d}` : undefined;
  }
  if (d.length === 10 && nanp(d)) return `+1${d}`;
  if (d.length === 11 && d.startsWith("1") && nanp(d.slice(1))) return `+${d}`;
  return undefined;
}

/** What the link says, as data: the link is built from it, and (Hit Em Up not installed) it is what waits for Hit Em Up's first launch. */
export interface HitemupPayload {
  v: 1;
  mode: "compose" | "group";
  to: string[];
  names: string[];
  group?: string;
  gref?: string;
  body?: string;
  source: typeof HITEMUP_SOURCE;
  ref: string;
}

export interface HitemupCompose {
  /** The link, or undefined when Hit Em Up isn't configured or a limit was passed (then `problem` says so). */
  href?: string;
  /** The same request as data (only with `href`). */
  payload?: HitemupPayload;
  /** People who had a number that couldn't be made E.164: left out of the link, and `note` names them. */
  skipped: string[];
  /** Everyone left out and why, one plain line each ("No phone for Maya", "Sam: the number doesn't look right"). */
  why: string[];
  /** Plain words for the sender when someone was left out. Never silent. */
  note?: string;
  /** Plain words when a limit stopped the link (more than 50 people, or a body over 1,000 characters). */
  problem?: string;
}

/**
 * Hit Em Up's compose link for these recipients and this text, from HITEMUP_COMPOSE_URL (e.g. "hitemup://compose"):
 * `?to=<E.164,…>&names=<name,…>&body=<text>&source=cleancleaver&ref=<id>[&group=<Business – Team>&gref=<id>]`
 * (docs/HITEMUP.md). The one place the link's shape lives. `names` is one name per `to` entry, same order (the team
 * record's display name, commas removed, 60 characters at most). `group` and `gref` go on a team of two or more, never
 * on a one-person nudge; the link never creates the group, the person's Send tap with Hit Em Up's visible "Save as
 * group" does. Only the business's own team numbers ever come in here. `ref` is the nag / hand-off id.
 *
 * `mode: "group"` is the Team screen's "Set up my team in Hit Em Up": the same link with `mode=group` first and no
 * body, for as many as 50 people with a valid number.
 *
 * Every phone goes through toE164; one that can't is left out and named in `note`. Past 50 recipients or 1,000
 * characters there is no link and `problem` says why: nothing is truncated. Unset: nothing. Someone with no phone
 * stored at all is not in `to` (Hit Em Up opens and the sender picks them); `why` lists them too.
 */
export function hitemupCompose(o: { composeUrl?: string; people: { name?: string; phone?: string }[]; body?: string; ref: string; group?: HitemupGroup; mode?: "group" }): HitemupCompose {
  if (!o.composeUrl) return { skipped: [], why: [] };
  const mode = o.mode === "group" ? "group" : "compose";
  const to: string[] = [];
  const names: string[] = [];
  const skipped: string[] = [];
  const why: string[] = [];
  for (const p of o.people) {
    const who = p.name ? firstName(p.name) : "Someone";
    if (!p.phone?.trim()) { why.push(`No phone for ${who}`); continue; }
    const e = toE164(p.phone);
    if (!e) { skipped.push(who); why.push(`${who}: the phone number doesn't look right`); continue; }
    if (!to.includes(e)) { to.push(e); names.push(linkName(p.name ?? "")); }
  }
  const note = skipped.length
    ? `${namesLine(skipped)} ${skipped.length === 1 ? "has a phone number that doesn't look right, so they're" : "have phone numbers that don't look right, so they're"} not in this ${mode === "group" ? "group" : "text"}. Fix ${skipped.length === 1 ? "it" : "them"} in Team.`
    : undefined;
  const base = { skipped, why, ...(note ? { note } : {}) };
  if (to.length > HITEMUP_MAX_RECIPIENTS) return { ...base, problem: `Hit Em Up takes up to ${HITEMUP_MAX_RECIPIENTS} people at a time, and this has ${to.length}. Send it in smaller groups.` };
  const body = mode === "group" ? undefined : (o.body ?? "");
  if (body !== undefined && body.length > HITEMUP_MAX_BODY) return { ...base, problem: `This message is ${body.length.toLocaleString("en-US")} characters, and Hit Em Up takes up to ${HITEMUP_MAX_BODY.toLocaleString("en-US")}. Shorten it.` };
  if (mode === "group" && (!to.length || !o.group)) return { ...base, problem: "Nobody on your team has a phone number Hit Em Up can use yet. Add numbers in Team." };
  const team = o.group && (mode === "group" || to.length >= 2) ? { group: hitemupGroupName(o.group), gref: `${o.group.locationId}:${o.group.kind}` } : undefined;
  const payload: HitemupPayload = { v: 1, mode, to, names, ...(team ?? {}), ...(body !== undefined ? { body } : {}), source: HITEMUP_SOURCE, ref: o.ref };
  const sep = o.composeUrl.includes("?") ? "&" : "?";
  const q = [
    ...(mode === "group" ? ["mode=group"] : []),
    ...(to.length ? [`to=${encodeURIComponent(to.join(","))}`] : []),
    ...(names.some(Boolean) ? [`names=${encodeURIComponent(names.join(","))}`] : []),
    ...(body !== undefined ? [`body=${encodeURIComponent(body)}`] : []),
    `source=${HITEMUP_SOURCE}`, `ref=${encodeURIComponent(o.ref)}`,
    ...(team ? [`group=${encodeURIComponent(team.group)}`, `gref=${encodeURIComponent(team.gref)}`] : []),
  ].join("&");
  return { ...base, href: `${o.composeUrl}${sep}${q}`, payload };
}

/** Just the link of `hitemupCompose` (no names: a bad number is dropped, but the sheet can't name who). Prefer `hitemupCompose`. */
export function hitemupComposeUrl(o: { composeUrl?: string; phones: (string | undefined)[]; body: string; ref: string }): string | undefined {
  return hitemupCompose({ composeUrl: o.composeUrl, people: o.phones.map((phone) => ({ phone })), body: o.body, ref: o.ref }).href;
}

/** "Set up my team in Hit Em Up" / "Update my team in Hit Em Up": the group link for the location's whole team. Ref: `group:<locationId>`. */
export function hitemupTeamSetup(o: { composeUrl?: string; team: { id: Id; name: string; phone?: string }[]; business: string; locationId: Id; last?: { employeeIds: Id[] } }): HitemupCompose & { label: string; preview?: string; ids: Id[] } {
  const c = hitemupCompose({ composeUrl: o.composeUrl, people: o.team, ref: `group:${o.locationId}`, mode: "group", group: { business: o.business, locationId: o.locationId, kind: "team" } });
  const ok = o.team.filter((p) => toE164(p.phone));
  const seen = new Set<string>();
  const inLink = ok.filter((p) => { const e = toE164(p.phone)!; if (seen.has(e)) return false; seen.add(e); return true; });
  const ids = inLink.map((p) => p.id);
  const preview = o.last ? hitemupGroupChanges(inLink, o.last.employeeIds) : undefined;
  return { ...c, label: o.last ? "Update my team in Hit Em Up" : "Set up my team in Hit Em Up", ...(preview ? { preview } : {}), ids };
}

/** "Adds Riley · removes 0": who is in the team link now against who was in it the last time it was used. */
export function hitemupGroupChanges(now: { id: Id; name: string }[], before: Id[]): string {
  const added = now.filter((p) => !before.includes(p.id)).map((p) => firstName(p.name));
  const removed = before.filter((id) => !now.some((p) => p.id === id)).length;
  return `Adds ${added.length ? namesLine(added) : "no one"} · removes ${removed}`;
}

/** The request back out of a link this file built (the phone gets links from the server, and needs the data to hand over). */
export function hitemupPayloadFromHref(href: string): HitemupPayload | undefined {
  let q: URLSearchParams;
  try { q = new URL(href).searchParams; } catch { return undefined; }
  const list = (k: string) => (q.get(k) ? q.get(k)!.split(",") : []);
  const ref = q.get("ref");
  if (!ref || q.get("source") !== HITEMUP_SOURCE) return undefined;
  const to = list("to");
  const names = list("names");
  const body = q.get("body");
  const group = q.get("group");
  const gref = q.get("gref");
  return { v: 1, mode: q.get("mode") === "group" ? "group" : "compose", to, names: to.map((_, k) => names[k] ?? ""), ...(group && gref ? { group, gref } : {}), ...(body !== null && q.get("mode") !== "group" ? { body } : {}), source: HITEMUP_SOURCE, ref };
}

/** How long a request waits for Hit Em Up's first launch (24 hours; docs/HITEMUP.md). */
export const HITEMUP_PENDING_HOURS = 24;
/** The key in the shared App Group's UserDefaults. */
export const HITEMUP_PENDING_KEY = "cleancleaver.pendingCompose";
export type HitemupPending = HitemupPayload & { createdAt: IsoDateTime; expiresAt: IsoDateTime };

/** The payload plus when it was made and when it expires: what is written to the shared App Group when Hit Em Up isn't installed. */
export function pendingCompose(p: HitemupPayload, now: Date): HitemupPending {
  return { ...p, createdAt: now.toISOString(), expiresAt: new Date(now.getTime() + HITEMUP_PENDING_HOURS * 3_600_000).toISOString() };
}

/** Does this look like a request Hit Em Up may act on? Same limits as the link; not expired. */
export function validPending(x: unknown, now: Date): x is HitemupPending {
  const p = x as Partial<HitemupPending> | null;
  if (!p || typeof p !== "object" || p.v !== 1 || (p.mode !== "compose" && p.mode !== "group") || p.source !== HITEMUP_SOURCE) return false;
  if (!Array.isArray(p.to) || !Array.isArray(p.names) || p.to.length > HITEMUP_MAX_RECIPIENTS || p.names.length !== p.to.length) return false;
  if (!p.to.every((t) => typeof t === "string" && /^\+[1-9]\d{7,14}$/.test(t)) || !p.names.every((n) => typeof n === "string" && n.length <= HITEMUP_MAX_NAME)) return false;
  if (typeof p.ref !== "string" || !p.ref || (p.body !== undefined && (typeof p.body !== "string" || p.body.length > HITEMUP_MAX_BODY))) return false;
  if (p.mode === "group" && (p.body !== undefined || !p.group || !p.gref || !p.to.length)) return false;
  if ((p.group !== undefined && (typeof p.group !== "string" || p.group.length > HITEMUP_MAX_NAME)) || (p.gref !== undefined && typeof p.gref !== "string")) return false;
  return typeof p.expiresAt === "string" && typeof p.createdAt === "string" && Date.parse(p.expiresAt) > now.getTime();
}

/** What the sheet says when Hit Em Up isn't connected yet. */
export const HITEMUP_NOT_CONNECTED = "Texting opens in Hit Em Up once it's connected. Copy the message for now.";

/* ─────────────── limits (N4) ─────────────── */

/** One nag already sent (a ShiftNotice of kind "nag"). */
export interface NagAttempt { toEmployeeId: Id; at: IsoDateTime; ref?: string; step?: NagStep }

const addMin = (iso: IsoDateTime, m: number) => new Date(Date.parse(iso) + m * 60_000).toISOString();

/** Can `personId` get another nag now? No if they got one inside the window (15 min urgent, 60 min normal). */
export function nagRateLimit(personId: Id, attempts: NagAttempt[], urgency: NagUrgency, now: IsoDateTime): { ok: true } | { ok: false; nextAt: IsoDateTime } {
  const w = NAG_WINDOW_MIN[urgency];
  const last = attempts.filter((a) => a.toEmployeeId === personId).map((a) => a.at).sort().pop();
  if (last && now < addMin(last, w)) return { ok: false, nextAt: addMin(last, w) };
  return { ok: true };
}

/** Outside open hours: a normal nag waits; an urgent one goes (N4). `open`/`close` are today's local hours as ISO times. */
export function quietNow(o: { urgency: NagUrgency; now: IsoDateTime; open: IsoDateTime; close: IsoDateTime; closed?: boolean }): boolean {
  if (o.urgency === "urgent") return false;
  // Closed all day (weekly hours): every normal nag waits.
  if (o.closed) return true;
  return o.now < o.open || o.now > o.close;
}

/* ─────────────── the ladder (N5) ─────────────── */

export type NagStep = "on_shift" | "person_in_charge" | "owner";

export interface NagLadderPeople {
  /** Available people on shift (nagTargets().available), in order. */
  onShift: NagPerson[];
  /** People in charge on shift and available (may overlap onShift). */
  personInCharge: NagPerson[];
  owners: NagPerson[];
}

export interface NagLadderState {
  state: "done" | "start" | "waiting" | "next" | "end";
  /** Who is next, and when (now, or when the window runs out). */
  next?: { person: NagPerson; step: NagStep; at: IsoDateTime };
  /** The last nag sent. */
  last?: NagAttempt;
  /** "Nagged Sam 10 min ago · next: Ana at 2:25 PM". */
  line: string;
}

/** Ladder order: everyone on shift, then the person in charge, then the owner; each person once. */
export function ladderOrder(p: NagLadderPeople): { person: NagPerson; step: NagStep }[] {
  const seen = new Set<Id>();
  const out: { person: NagPerson; step: NagStep }[] = [];
  const add = (list: NagPerson[], step: NagStep) => { for (const x of list) if (!seen.has(x.id)) { seen.add(x.id); out.push({ person: x, step }); } };
  add(p.onShift.filter((x) => !x.isPersonInCharge), "on_shift");
  add(p.personInCharge, "person_in_charge");
  add(p.onShift, "person_in_charge");
  add(p.owners, "owner");
  return out;
}

function ago(from: IsoDateTime, now: IsoDateTime): string {
  const m = Math.max(0, Math.round((Date.parse(now) - Date.parse(from)) / 60_000));
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  return `${h} h ${m % 60 ? `${m % 60} min ` : ""}ago`;
}

/**
 * The next step when nobody finished in the window (urgent 15 min, normal 60 min). Done (the record exists) stops it.
 * `timeLabel` formats an ISO time as the location's clock ("2:25 PM").
 */
export function nagLadder(item: Pick<NagItem, "urgency" | "done">, attempts: NagAttempt[], now: IsoDateTime | Date, people: NagLadderPeople, timeLabel: (iso: IsoDateTime) => string = (iso) => iso.slice(11, 16)): NagLadderState {
  const nowIso = typeof now === "string" ? now : now.toISOString();
  const sorted = [...attempts].sort((a, b) => a.at.localeCompare(b.at));
  const last = sorted[sorted.length - 1];
  const nameOf = (id: Id) => [...people.onShift, ...people.personInCharge, ...people.owners].find((p) => p.id === id)?.name;
  const lastBit = last ? `Nagged ${firstName(nameOf(last.toEmployeeId) ?? "someone")} ${ago(last.at, nowIso)}` : "";
  if (item.done) return { state: "done", ...(last ? { last } : {}), line: last ? `Done. ${lastBit}.` : "Done." };
  const asked = new Set(sorted.map((a) => a.toEmployeeId));
  const order = ladderOrder(people).filter((x) => !asked.has(x.person.id));
  const nextUp = order[0];
  if (!last) {
    if (!nextUp) return { state: "end", line: "Nobody on shift can do this now. Do it yourself, or call someone in." };
    return { state: "start", next: { ...nextUp, at: nowIso }, line: `Suggested: ${firstName(nextUp.person.name)}` };
  }
  const due = addMin(last.at, NAG_WINDOW_MIN[item.urgency]);
  if (!nextUp) return { state: "end", last, line: `${lastBit} · Everyone on the ladder has been asked. Call them, or do it yourself.` };
  const waiting = nowIso < due;
  return {
    state: waiting ? "waiting" : "next", last, next: { ...nextUp, at: waiting ? due : nowIso },
    line: `${lastBit} · next: ${firstName(nextUp.person.name)}${waiting ? ` at ${timeLabel(due)}` : " now"}`,
  };
}

/* ─────────────── Text the buyer (the inverse) ─────────────── */

/**
 * The Order list (core ordering.ts OrderGroup) as a text to whoever does the buying: plain lines, the supplier's
 * number or page when known. Only items not yet ordered; undefined when there's nothing left to order.
 */
export function buyerMessage(g: { title: string; supplier?: { name: string; phone?: string; url?: string }; items: { name: string; qty?: number; unit?: string; ordered?: unknown }[] }, o: { to?: string; from?: string; place?: string } = {}): string | undefined {
  const open = g.items.filter((i) => !i.ordered);
  if (!open.length) return undefined;
  const lines = open.map((i) => `• ${i.name}${i.qty ? ` × ${i.qty}${i.unit ? ` (${i.unit})` : ""}` : " (how many? check the shelf)"}`);
  const where = g.supplier?.phone ? ` Their number: ${g.supplier.phone}.` : g.supplier?.url ? ` Order page: ${g.supplier.url}` : "";
  const hi = o.to ? `Hi ${firstName(o.to)}, ` : "Hi, ";
  const from = g.supplier ? `from ${g.supplier.name}` : "(no supplier set yet)";
  const sign = o.from ? `\n— ${firstName(o.from)}` : "";
  return `${hi}please order ${from}${o.place ? ` for ${o.place}` : ""}:\n${lines.join("\n")}${where ? `\n${where.trim()}` : ""}\nThanks!${sign}`;
}

/* ─────────────── the sheet (one builder for the API and both demos, T7) ─────────────── */

/** One ready text. `href`: Hit Em Up's compose link, only when HITEMUP_COMPOSE_URL is set (otherwise: copy the body). */
export interface NagText { employeeId: Id; name: string; body: string; href?: string; hasPhone: boolean; /** Their number is stored but isn't a phone number: not in the link, and this says so. */ note?: string; /** A Hit Em Up limit stopped the link (never truncated). */ problem?: string }

/** What the Nag sheet shows and what POST /nags answers: GET and POST, the server and both demo mocks, build it here. */
export interface NagView {
  itemRef: string;
  title: string;
  urgency: NagUrgency;
  done: boolean;
  /** Who can do it now, suggested first. `heldUntil`: nagged inside the window, next nag allowed then (N4). */
  available: { employeeId: Id; name: string; hasPhone: boolean; heldUntil?: IsoDateTime }[];
  /** On shift but not available: the words "Not available today" only (N2). */
  unavailable: { employeeId: Id; name: string; line: string }[];
  suggested?: { employeeId: Id; name: string };
  /** One ready text per available person (the suggested person first). */
  texts: NagText[];
  /** One group text when two or more available people have a phone; per-person texts stay the fallback. */
  group?: { body: string; href?: string; note?: string; problem?: string };
  /** A normal nag outside open hours waits (N4): the plain reason. */
  quiet?: string;
  /** Nags already sent for this item today, newest first. */
  history: { toEmployeeId: Id; name: string; at: IsoDateTime; fromName?: string }[];
  ladder: { state: NagLadderState["state"]; line: string; next?: { employeeId: Id; name: string; step: NagStep; at: IsoDateTime } };
}

export interface NagViewInput {
  item: NagItem;
  onShift: NagPerson[];
  clearance: Record<Id, boolean>;
  leave?: NagLeave[];
  owners: NagPerson[];
  fromId?: Id;
  fromName?: string;
  /** Today's nags at this place (every item: the rate limit is per person). */
  attempts: (NagAttempt & { ref: string; fromName?: string })[];
  now: IsoDateTime;
  today?: IsoDate;
  timeLabel?: (iso: IsoDateTime) => string;
  /** Today's open hours as ISO times (quiet hours); `closed`: closed all day (weekly hours). */
  hours?: { open: IsoDateTime; close: IsoDateTime; closed?: true };
  /** The web base for links (tests, staging). */
  base?: string;
  /** The business and place, for the group name and id in a team link. */
  place?: { business: string; locationId: Id };
  /** HITEMUP_COMPOSE_URL: set, every text carries Hit Em Up's compose link; unset, none does. */
  composeUrl?: string;
}

const teamOf = (i: Pick<NagViewInput, "place">): HitemupGroup | undefined => (i.place ? { business: i.place.business, locationId: i.place.locationId, kind: "on-shift" } : undefined);

export function nagView(i: NagViewInput): NagView {
  const clock = i.timeLabel ?? ((iso: IsoDateTime) => iso.slice(11, 16));
  const t = nagTargets({ item: i.item, onShift: i.onShift, clearance: i.clearance, leave: i.leave, now: i.now, today: i.today, fromId: i.fromId });
  const pics = t.available.filter((p) => p.isPersonInCharge);
  const owners = i.owners.filter((o) => o.id !== i.fromId && !t.unavailable.some((u) => u.id === o.id));
  const mine = i.attempts.filter((a) => a.ref === i.item.ref);
  const ladder = nagLadder(i.item, mine, i.now, { onShift: t.available, personInCharge: pics, owners }, clock);
  const link = nagLink(i.item.ref, "web", i.base);
  // The ladder's next person is the one suggested; otherwise the first available.
  const suggestedId = ladder.next?.person.id ?? t.available[0]?.id;
  const people = [...t.available];
  if (ladder.next && !people.some((p) => p.id === ladder.next!.person.id)) people.push(ladder.next.person);
  people.sort((a, b) => Number(b.id === suggestedId) - Number(a.id === suggestedId));
  const texts = people.map((p) => {
    const body = nagMessage({ item: i.item, person: p, from: i.fromName, urgency: i.item.urgency, link });
    const c = hitemupCompose({ composeUrl: i.composeUrl, people: [p], body, ref: i.item.ref });
    return { employeeId: p.id, name: p.name, body, hasPhone: !!p.phone, ...(c.href ? { href: c.href } : {}), ...(c.note ? { note: c.note } : {}), ...(c.problem ? { problem: c.problem } : {}) };
  });
  const withPhone = t.available.filter((p) => p.phone);
  const groupBody = withPhone.length >= 2 ? nagMessage({ item: i.item, person: { names: withPhone.map((p) => p.name) }, from: i.fromName, urgency: i.item.urgency, link }) : undefined;
  const group = groupBody ? hitemupCompose({ composeUrl: i.composeUrl, people: withPhone, body: groupBody, ref: i.item.ref, group: teamOf(i) }) : undefined;
  const quiet = i.hours && quietNow({ urgency: i.item.urgency, now: i.now, ...i.hours })
    ? `${i.hours.closed ? "You're closed today" : `It's outside open hours (${clock(i.hours.open)} to ${clock(i.hours.close)})`}. A normal nag waits until you're open; a safety problem can go now.`
    : undefined;
  const nameOf = (id: Id) => [...i.onShift, ...i.owners].find((p) => p.id === id)?.name ?? "Someone";
  const suggested = people.find((p) => p.id === suggestedId);
  return {
    itemRef: i.item.ref, title: i.item.title, urgency: i.item.urgency, done: !!i.item.done,
    available: people.map((p) => {
      const r = nagRateLimit(p.id, i.attempts, i.item.urgency, i.now);
      return { employeeId: p.id, name: p.name, hasPhone: !!p.phone, ...(r.ok ? {} : { heldUntil: r.nextAt }) };
    }),
    unavailable: t.unavailable.map((u) => ({ employeeId: u.id, name: u.name, line: u.line })),
    ...(suggested ? { suggested: { employeeId: suggested.id, name: suggested.name } } : {}),
    texts,
    ...(groupBody ? { group: { body: groupBody, ...(group?.href ? { href: group.href } : {}), ...(group?.note ? { note: group.note } : {}), ...(group?.problem ? { problem: group.problem } : {}) } } : {}),
    ...(quiet ? { quiet } : {}),
    history: [...mine].sort((a, b) => b.at.localeCompare(a.at)).map((a) => ({ toEmployeeId: a.toEmployeeId, name: nameOf(a.toEmployeeId), at: a.at, ...(a.fromName ? { fromName: a.fromName } : {}) })),
    ladder: { state: ladder.state, line: ladder.line, ...(ladder.next ? { next: { employeeId: ladder.next.person.id, name: ladder.next.person.name, step: ladder.next.step, at: ladder.next.at } } : {}) },
  };
}

/** The title and body of the notice row a nag writes (the recipient's For You shows it; a future push sends it). */
export function nagNoticeText(o: { item: Pick<NagItem, "title" | "urgency" | "problem">; fromName?: string; body: string }): { title: string; body: string } {
  const who = o.fromName ? firstName(o.fromName) : "Your manager";
  return { title: o.item.urgency === "urgent" ? `Urgent from ${who}: ${(o.item.problem ?? o.item.title).replace(/[.\s]+$/, "")}` : `${who} asked you: ${o.item.title}`, body: o.body.slice(0, 600) };
}

/* ─────────────── from a day (the API and both demos read the same data the same way) ─────────────── */

export type NagItemResult = { ok: true; item: NagItem } | { ok: false; status: 400 | 404; message: string };

/**
 * The item a ref names at one location today, with `done` from its record (N5). `incident`: the report a
 * report ref names, when it's at this location. A sick report is refused as private (T4).
 */
export function nagItemFromDay(ref: string, ctx: DayContext, o: { incident?: { id: Id; title: string; severity: string; type: string; status: string }; urgency?: NagUrgency } = {}): NagItemResult {
  const p = parseItemRef(ref);
  if (!p) return { ok: false, status: 400, message: '"itemRef" must look like check:<key>, fix:<entry id> or report:<report id>.' };
  let item: NagItem | undefined;
  if (p.kind === "check") {
    const e = expectedChecks(ctx).find((x) => x.key === p.id);
    if (!e) return { ok: false, status: 404, message: "That check wasn't found." };
    item = checkNagItem(e, { dueLabel: ctx.timeLabel?.(e.dueAt), done: isSatisfied(e, ctx.entries) });
  } else if (p.kind === "fix") {
    const r = ctx.entries.find((x) => x.id === p.id);
    if (!r || r.evaluation.ok) return { ok: false, status: 404, message: "That problem wasn't found." };
    const title = getTemplate(r.templateId)?.title.split(/[:,(]/)[0]!.trim() ?? r.templateId;
    item = fixNagItem(r.id, title, r.evaluation.violations.map((v) => v.ruleId), { done: !!r.correctiveAction });
  } else if (p.kind === "report") {
    const i = o.incident;
    if (!i || i.id !== p.id) return { ok: false, status: 404, message: "That report wasn't found." };
    item = reportNagItem(i.id, i.title, i.severity, { type: i.type, done: i.status === "resolved" });
    if (!item) return { ok: false, status: 400, message: "That report is private. Only the person in charge handles it." };
  } else return { ok: false, status: 400, message: "Chores are nagged from the home, not the business." };
  // A manager may raise a nag to urgent; a safety problem is never lowered (N3).
  return { ok: true, item: o.urgency === "urgent" && item.urgency !== "urgent" ? { ...item, urgency: "urgent", problem: item.title } : item };
}

/** Who's on shift and cleared today (shiftGate), the owners, and who's away, from a day and the location's people. */
export function nagPeopleFromDay(ctx: DayContext, employees: { id: Id; name: string; phone?: string; isPersonInCharge?: boolean; endsOn?: IsoDate }[], ownerEmployeeIds: Iterable<Id>): Pick<NagViewInput, "onShift" | "clearance" | "owners" | "leave" | "today" | "timeLabel" | "hours"> {
  const person = (e: { id: Id; name: string; phone?: string; isPersonInCharge?: boolean }): NagPerson => ({ id: e.id, name: e.name, ...(e.phone ? { phone: e.phone } : {}), ...(e.isPersonInCharge ? { isPersonInCharge: true } : {}) });
  const owners = new Set(ownerEmployeeIds);
  return {
    onShift: ctx.onShift.map(person),
    clearance: Object.fromEntries(ctx.onShift.map((p) => [p.id, shiftGate(ctx, p.id).ready])),
    owners: employees.filter((e) => owners.has(e.id)).map(person),
    // A one-event volunteer past their last day is away (no leave table exists yet).
    leave: employees.filter((e) => e.endsOn && e.endsOn < ctx.date).map((e) => ({ employeeId: e.id, from: e.endsOn! })),
    today: ctx.date,
    ...(ctx.timeLabel ? { timeLabel: ctx.timeLabel } : {}),
    // Weekly hours: the day's own hours, closed days, and an overnight shift still running past midnight (hours.ts).
    hours: shiftHoursIso(ctx, ctx.date, ctx.now, ctx.toIso),
  };
}

/** The nag records (ShiftNotice rows of kind "nag") at one place today, as attempts. */
export function nagAttempts(rows: { kind: string; refId?: string; toEmployeeId: Id; fromEmployeeId?: Id; createdAt: IsoDateTime }[], names: Map<Id, string>): NagViewInput["attempts"] {
  return rows.filter((r) => r.kind === "nag").map((r) => ({ toEmployeeId: r.toEmployeeId, at: r.createdAt, ref: r.refId ?? "", ...(r.fromEmployeeId && names.get(r.fromEmployeeId) ? { fromName: names.get(r.fromEmployeeId)! } : {}) }));
}

/**
 * POST /nags as one decision: who gets a text now, who is held by the rate limit, or why nothing can go. The caller
 * records one notice per `send` and answers `send`'s links.
 */
export function nagDecision(i: NagViewInput, personIds: Id[], names: Map<Id, string>): { ok: true; view: NagView; send: NagText[]; held: { employeeId: Id; name: string; nextAt: IsoDateTime }[] } | { ok: false; status: 400 | 429; message: string } {
  if (i.item.done) return { ok: false, status: 400, message: "That's already done. Nobody needs a nag." };
  const view = nagView(i);
  const allowed = new Map(view.texts.map((t) => [t.employeeId, t]));
  // Only people who can do it now, or the ladder's next step: never someone unavailable, and never why (N1, N2).
  const refused = personIds.filter((id) => !allowed.has(id));
  if (refused.length) {
    const n = names.get(refused[0]!);
    return { ok: false, status: 400, message: `${n ? firstName(n) : "That person"} is not available today. Pick someone else.` };
  }
  if (view.quiet) return { ok: false, status: 400, message: view.quiet };
  const send: NagText[] = [];
  const held: { employeeId: Id; name: string; nextAt: IsoDateTime }[] = [];
  for (const id of personIds) {
    const t = allowed.get(id)!;
    const r = nagRateLimit(id, i.attempts, i.item.urgency, i.now);
    if (r.ok) send.push(t); else held.push({ employeeId: id, name: t.name, nextAt: r.nextAt });
  }
  if (!send.length) {
    const h = held[0]!;
    const at = (i.timeLabel ?? ((iso: IsoDateTime) => iso.slice(11, 16)))(h.nextAt);
    return { ok: false, status: 429, message: `${firstName(h.name)} was nagged a moment ago. You can nag again at ${at}.` };
  }
  return { ok: true, view, send, held };
}

/** After recording: the sheet again (history, ladder), the texts just sent, and one group text for them. */
export function nagSent(i: NagViewInput, send: NagText[], held: { employeeId: Id; name: string; nextAt: IsoDateTime }[]): NagView & { sent: NagText[]; held: typeof held; sentGroup?: { body: string; href?: string; note?: string; problem?: string } } {
  const after = nagView({ ...i, attempts: [...i.attempts, ...send.map((t) => ({ toEmployeeId: t.employeeId, at: i.now, ref: i.item.ref, ...(i.fromName ? { fromName: i.fromName } : {}) }))] });
  const sent = after.texts.filter((t) => send.some((s) => s.employeeId === t.employeeId));
  const phones = i.onShift.concat(i.owners).filter((p, k, all) => p.phone && sent.some((t) => t.employeeId === p.id) && all.findIndex((x) => x.id === p.id) === k);
  const body = phones.length >= 2 ? nagMessage({ item: i.item, person: { names: phones.map((p) => p.name) }, from: i.fromName, urgency: i.item.urgency, link: nagLink(i.item.ref, "web", i.base) }) : undefined;
  const g = body ? hitemupCompose({ composeUrl: i.composeUrl, people: phones, body, ref: i.item.ref, group: teamOf(i) }) : undefined;
  return { ...after, sent, held, ...(body ? { sentGroup: { body, ...(g?.href ? { href: g.href } : {}), ...(g?.note ? { note: g.note } : {}), ...(g?.problem ? { problem: g.problem } : {}) } } : {}) };
}
